import styles from './ManageUserTable.module.scss';
import { useMemo, useState, useEffect } from 'react';
import loaderSvg from '../../../../assets/aica-loader.svg';
import clsx from 'clsx';
import { Headers, Rows } from '../useManageUser';
import ActionMenu from '../../../../common/ActionMenu/ActionMenu';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { fetchInternalSummonAicaData, InternalSummonAicaDataPayload, InternalSummonUserType } from '../../../../store/user/actions';
import { getPlatformUserId, openUserSummonTab, truncateByCharacters } from '../../../../utils/utils';
import isNil from 'lodash/isNil';
import { useSearchParams } from 'react-router-dom';
import { MANAGE_USERS_TABS } from '../../../../store/investor/constants';
import { CompanySideHeaders } from '../ManageUserConstants';
import { LENDER_ROUTES } from '../../../../utils/constants/routesConst';
import { USER_TYPE } from '../../../../utils/constants/user';
import { debtProductsEmpty } from '../../../../assets/hostedassets';
import { fetchCountryCodes } from '../../../../store/company/apiHelpers';
import EditUserModal from './components/EditUserModal';
import DeleteUserModal from './components/DeleteUserModal';
import { setEditUserModal, setDeleteUserModal } from '../../../../store/investor/action';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_PARTNER_SUMMON } from '../../../../utils/constants/mixpanelEvents/investorEvents';

const { PARTNER, INVESTOR, TEAM } = MANAGE_USERS_TABS;

type ManageUserTableProps = {
    rows: Rows;
    headers: Headers;
    isRowsLoading: boolean;
    setShowAddUserModal: React.Dispatch<React.SetStateAction<boolean>>;
    setCurrentRowUser: React.Dispatch<React.SetStateAction<any>>;
    isCompanySide: boolean;
    onUsersUpdate?: (updatedUsers: any[]) => void;
};

const ManageUserTable = ({
    rows,
    headers,
    isRowsLoading,
    setShowAddUserModal,
    setCurrentRowUser,
    isCompanySide,
    onUsersUpdate,
}: ManageUserTableProps) => {
    const dispatch = useAppDispatch();
    const isSummonUser = !isNil(sessionStorage.getItem('auth_token'));
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const user = useAppSelector((state) => state.user);
    const editUserModal = useAppSelector((state) => state.investor.editUserModal);
    const deleteUserModal = useAppSelector((state) => state.investor.deleteUserModal);
    const [params, setParams] = useSearchParams();
    const [countryCodes, setCountryCodes] = useState<any[]>([]);

    const getSummonUserType = (userType: string) => {
        switch (userType) {
            case 'Partner':
                return InternalSummonUserType.PARTNER;
            case 'Lender':
                return InternalSummonUserType.LENDER;
            case 'Team member':
                return InternalSummonUserType.ADMIN_TEAM;
            default:
                return null;
        }
    };

    const ACTION_MENU_ITEMS = {
        'View Account': {
            action: ({ userId, userType, organizationId }, source) => {
                const platformUserId = getPlatformUserId();
                const summonUserType = getSummonUserType(userType);
                if (!summonUserType) {
                    console.error('Invalid user type');
                    return;
                }

                Mixpanel.track(AICA_PARTNER_SUMMON, {
                    'User Role': user.userData.accountType,
                    'User id': user.userData.userId,
                    'Current URL': window.location.href,
                    'User Email id': user.userData.investorPrimaryEmail,
                    'Partner OrgId': organizationId,
                    'Investor OrgId': platformData?.investorOrgId,
                    'Access detail': 'Summon View',
                });

                const payload: InternalSummonAicaDataPayload = {
                    parentOrgId: platformData?.investorOrgId as string,
                    parentEmailId: user.userData.investorPrimaryEmail,
                    userId,
                    userType: summonUserType,
                    requesterUserId: platformUserId,
                };
                dispatch(
                    fetchInternalSummonAicaData(payload, {
                        onSuccess: (userData) => {
                            openUserSummonTab({ ...userData, orgId: platformData?.investorOrgId });
                        },
                    }),
                );
            },
        },
        'Add User': {
            action: ({ userId, organizationId, userType }, source) => {
                setCurrentRowUser({
                    organizationId,
                    userId,
                    userType,
                });
                setShowAddUserModal(true);
            },
        },
        'Investment Preferences': {
            action: ({ userId, userType }, source) => {
                const platformUserId = getPlatformUserId();
                const summonUserType = getSummonUserType(userType);
                if (!summonUserType) {
                    console.error('Invalid user type');
                    return;
                }
                const payload: InternalSummonAicaDataPayload = {
                    parentOrgId: platformData?.investorOrgId as string,
                    parentEmailId: user.userData.investorPrimaryEmail,
                    userId,
                    userType: summonUserType,
                    requesterUserId: platformUserId,
                };
                dispatch(
                    fetchInternalSummonAicaData(payload, {
                        onSuccess: (userData) => {
                            openUserSummonTab({
                                ...userData,
                                orgId: platformData?.investorOrgId,
                                referrer: LENDER_ROUTES.MANAGE_ELIGIBILITY,
                            });
                        },
                    }),
                );
            },
        },
        'Edit User': {
            action: ({ userId, rowData }, source) => {
                dispatch(setEditUserModal(true, rowData));
            },
        },
        'Delete User': {
            action: ({ userId, rowData }, source) => {
                dispatch(setDeleteUserModal(true, rowData));
            },
        },
    };

    const getActionCtaVal = (actionCta: string[] | undefined) => {
        if (!actionCta) return [];

        return actionCta.map((cta) => {
            if (cta === 'SUMMON') return 'View Account';
            if (cta === 'ADD_USER') return 'Add User';
            if (cta === 'INVESTMENT_PREFERENCE') return 'Investment Preferences';
            if (cta === 'DELETE_USER') return 'Delete User';
            return '';
        });
    };

    useEffect(() => {
        fetchCountryCodes({ onSuccess: (list) => setCountryCodes(list) });
    }, []);

    const shouldShowUserTypeColumn = () => {
        const category = params.get('category')?.toString() ?? '';
        return [TEAM, INVESTOR].includes(category);
    };

    const renderTableContent = () => {
        return (
            <table className={styles.Table}>
                <thead className={styles.TableHead}>
                    <tr className={styles.Header}>
                        {headers.map((header, i) => {
                            if (!shouldShowUserTypeColumn() && header.label === 'User Type') {
                                return null;
                            }

                            if (isCompanySide && header.label === 'Actions' && user.userData?.accountType !== USER_TYPE.INVESTOR) {
                                return null;
                            }
                            return (
                                <th
                                    key={header.id}
                                    className={clsx({
                                        [styles.HeadMain]: i === 0,
                                        [styles.ActionColumn]: header.label === 'Actions',
                                        [styles.HeadItem]: i !== 0,
                                        [styles.UserType]: header.label === 'User Type' && params.get('category')?.toString() === INVESTOR,
                                    })}
                                    style={isCompanySide ? { borderRight: 'none' } : {}}
                                >
                                    {header.label !== 'Actions' ? header.label : ''}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className={styles.TableBody}>
                    {isRowsLoading ? (
                        <tr>
                            <td colSpan={headers.length}>
                                <div className={styles.LoadingContainer}>
                                    <img style={{ zIndex: 1000 }} src={loaderSvg} alt="Loading..." width={'80px'} />
                                </div>
                            </td>
                        </tr>
                    ) : !Array.isArray(rows) || rows.length === 0 ? (
                        <tr>
                            <td colSpan={headers.length}>
                                <div className={styles.EmptyState}>
                                    <img src={debtProductsEmpty} alt="Empty" />
                                    <p>No users found</p>
                                </div>
                            </td>
                        </tr>
                    ) : isCompanySide ? (
                        rows?.map((row) => (
                            <tr key={row.userId} className={styles.Row} style={{ width: '100%' }}>
                                {CompanySideHeaders.map((header) => {
                                    if (header.type === 'static') {
                                        if (header.label === 'User Type' && !shouldShowUserTypeColumn()) {
                                            return null;
                                        }

                                        if (header.label === 'Actions' && user.userData?.accountType !== USER_TYPE.INVESTOR) {
                                            return null;
                                        }

                                        if (header.label === 'Actions') {
                                            const isSelf = row.userId === user.userData?.userId;
                                            const actionConfig = isSelf ? ['Edit User'] : ['Edit User', 'Delete User'];
                                            return (
                                                <td className={clsx(styles.RowItem, { [styles.ActionColumn]: true })} key={header.id}>
                                                    <ActionMenu
                                                        triggerPayload={{
                                                            userId: row.userId,
                                                            rowData: row,
                                                        }}
                                                        rowData={{
                                                            mainConfig: {
                                                                actionConfig,
                                                            },
                                                        }}
                                                        actionMenu={ACTION_MENU_ITEMS}
                                                        source="EDIT_USERS"
                                                        disabled={false}
                                                        usePortal={true}
                                                    />
                                                </td>
                                            );
                                        }

                                        const value =
                                            header.label === 'User Name'
                                                ? row.name
                                                : header.label === 'Email'
                                                ? row.emailId
                                                : header.label === 'Phone Number'
                                                ? row.mobileNumber
                                                : header.label === 'Job Role'
                                                ? row.customFields?.jobRole || '-'
                                                : header.label === 'User Type'
                                                ? row.userType?.toLowerCase() === 'lender'
                                                    ? 'Admin'
                                                    : row.userType?.toLowerCase() === 'lender_team'
                                                    ? 'Team Member'
                                                    : ''
                                                : row.userStatus;

                                        return (
                                            <td
                                                className={clsx(styles.RowItem, {
                                                    [styles.UserTypeRow]:
                                                        header.label === 'User Type' && params.get('category')?.toString() === INVESTOR,
                                                })}
                                                title={value || '-'}
                                                key={header.id}
                                                style={{
                                                    borderRight: 'none',
                                                    borderLeft: 'none',
                                                }}
                                            >
                                                {header.label === 'Status' ? (
                                                    <span
                                                        className={clsx({
                                                            [styles.Verified]: value === 'Verified',
                                                            [styles.Unverified]: value === 'Unverified',
                                                        })}
                                                    >
                                                        {value}
                                                    </span>
                                                ) : (
                                                    <span className={styles.CopyableCell}>
                                                        <span className={styles.FullText}>{value || '-'}</span>
                                                        <span className={styles.TruncatedDisplay}>
                                                            {truncateByCharacters(value, 20) || '-'}
                                                        </span>
                                                    </span>
                                                )}
                                            </td>
                                        );
                                    }
                                    return null;
                                })}
                            </tr>
                        ))
                    ) : (
                        rows?.map((row) => (
                            <tr key={row.userId} className={styles.Row}>
                                <td className={styles.RowItem} title={row.name}>
                                    {row.name}
                                </td>
                                <td className={styles.RowItem} title={row.emailId}>
                                    {row.emailId}
                                </td>
                                <td className={styles.RowItem} title={row.mobileNumber}>
                                    {row.mobileNumber}
                                </td>
                                <td className={styles.RowItem} title={row.customFields?.jobRole || '-'}>
                                    {row.customFields?.jobRole || '-'}
                                </td>
                                {params.get('category')?.toString() === TEAM && (
                                    <td className={styles.RowItem} title={row.userType}>
                                        {row.userType}
                                    </td>
                                )}
                                <td className={styles.RowItem} title={row.userStatus}>
                                    <span
                                        className={clsx({
                                            [styles.Verified]: row.userStatus === 'Verified',
                                            [styles.Unverified]: row.userStatus === 'Unverified',
                                        })}
                                    >
                                        {row.userStatus}
                                    </span>
                                </td>
                                <td className={styles.RowItem} title={row.organizationName ?? '-'}>
                                    {row.organizationName === '' ? '-' : row.organizationName}
                                </td>
                                {headers.map((header) => {
                                    if (header.type === 'dynamic') {
                                        return (
                                            <td
                                                className={styles.RowItem}
                                                title={row.additionalDetails[header.key ?? ''] ?? '-'}
                                                key={header.id}
                                            >
                                                {row.additionalDetails[header.key ?? ''] ?? '-'}
                                            </td>
                                        );
                                    }
                                    return null;
                                })}
                                <td className={styles.RowItem}>
                                    <ActionMenu
                                        triggerPayload={{
                                            userId: row.userId,
                                            organizationId: row.organizationId,
                                            userType: row.userType,
                                            rowData: row,
                                        }}
                                        rowData={{
                                            mainConfig: {
                                                actionConfig: getActionCtaVal(row?.actionCta),
                                            },
                                        }}
                                        actionMenu={ACTION_MENU_ITEMS}
                                        source="MANAGE_USERS"
                                        disabled={!row?.actionCta?.length || isSummonUser}
                                        usePortal={true}
                                    />
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        );
    };

    return (
        <>
            <div className={styles.TableContainer}>{renderTableContent()}</div>
            {editUserModal.show && (
                <EditUserModal
                    isOpen={editUserModal.show}
                    onClose={() => {
                        dispatch(setEditUserModal(false));
                    }}
                    allRowsData={rows}
                    userData={editUserModal.userData}
                    onUsersUpdate={onUsersUpdate}
                    countryCodes={countryCodes}
                />
            )}
            {deleteUserModal.show && (
                <DeleteUserModal
                    isOpen={deleteUserModal.show}
                    onClose={() => {
                        dispatch(setDeleteUserModal(false));
                    }}
                    userData={deleteUserModal.userData}
                    onUsersUpdate={onUsersUpdate}
                    allRowsData={rows}
                />
            )}
        </>
    );
};

export default ManageUserTable;
